import {
    ChartTypes,
    TimeSeriesItem,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQuery,
    ApiQueryEndpoints,
    Filter,
} from "~/modules/filters/types";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { ExportTableNames } from "~/modules/downloads/types";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";

// // // //

/**
 * Represents the state of a control that can be either enabled or active.
 */
export interface BooleanControlState {
    enabled: boolean;
    active: boolean;
    forceUpdate?: boolean;
}

/**
 * Represents the state of the navigator's X-axis
 * Set min/max values here to adjust the default window of the Visualization navigator
 */
export interface NavigatorXAxisState {
    min: number | null;
    max: number | null;
    // Example:
    // min: Date.UTC(2022, 0, 1), // January 1, 2022
    // max: Date.UTC(2022, 11, 31) // December 31, 2022
}

/**
 * Defines the state of the navigator control within a visualization component.
 * - `enabled`: Indicates whether the navigator control is enabled.
 * - `active`: Indicates whether the navigator control is currently active or in use.
 * - `forceUpdate`: Indicates whether the navigator will force update when a mode is changed
 * - `xAxis`: (Optional) Specifies the default min/max values of the navigator
 */
export interface NavigatorControlState extends BooleanControlState {
    xAxis?: NavigatorXAxisState;
}

/**
 * A single option in a timeseries segment select control.
 * - `filters`: layered onto the timeseries query (AND) when this option is
 *   selected. An empty array means "no additional filtering" (the All option).
 */
export interface TimeseriesSegmentSelectOption {
    label: string;
    value: string;
    tooltip?: string;
    loginRequired?: boolean;
    filters: Filter[];
}

/**
 * Configures a generic, filter-carrying segment select control for a
 * timeseries chart (e.g. the stocks page's "Underlying Type" control that
 * splits the universe into ETF/ETP vs. Equity/Stock). Each option carries the
 * query filters it applies, so shared chart components stay agnostic of what
 * is being segmented.
 */
export interface TimeseriesSegmentSelectProps {
    /** Control label rendered above the select (e.g. "Underlying Type"). */
    label: string;
    /** Value of the option selected by default. */
    defaultValue: string;
    options: TimeseriesSegmentSelectOption[];
}

/**
 * Configures the controls for a visualization
 * - `stacked`: (Optional) Control for stacking data series.
 * - `legend`: (Optional) Control for displaying the legend.
 * - `navigator`: (Optional) Control for navigating the data range.
 */
export interface VisualizationControlProps {
    stacked?: BooleanControlState;
    legend?: BooleanControlState;
    navigator?: NavigatorControlState;
}

// // // //

/**
 * Represents a control with enabled and active states, along with a method to toggle its active state
 */
interface ToggleableControl {
    enabled: boolean;
    active: boolean;
    toggle: () => void;
}

/**
 * Extends toggleable control features with navigator-specific properties for a visualization component
 */
interface NavigatorControls extends ToggleableControl {
    xAxis?: NavigatorXAxisState;
}

/**
 * Represents the state of various controls within a visualization component, including stacked, legend, and navigator controls.
 */
export interface VisualizationControlStates {
    stacked: ToggleableControl;
    legend: ToggleableControl;
    navigator: NavigatorControls;
}

// // // //

/**
 * Defines the properties for a Select control for a visualization
 * Properties:
 * - `enabled`: Indicates if the control is enabled.
 * - `value`: The current value selected for the control.
 * - `allowedOptions`: An array of allowed values from which the user can select.
 * - `labelOverrides`: Optional mapping to override default labels for specific options.
 * - `newOptions`: Optional options to call out as newly added with a pulsing dot.
 */
export interface SelectControlProps<T extends string | number | symbol> {
    enabled: boolean;
    value: T;
    allowedOptions: T[];
    forceUpdate?: boolean;
    labelOverrides?: Partial<Record<T, string>>;
    optionsWithNewIndicator?: T[];
}

/**
 * Specifies the optional controls for configuring aggregate queries in a visualization component
 * Properties:
 * - `groupBy`: (Optional) Control for selecting the grouping criterion.
 * - `aggregateFunction`: (Optional) Control for selecting the aggregate function.
 */
export interface AggregateQueryControlsProps {
    groupBy?:
        | SelectControlProps<TokenTimeseriesGroupBy>
        | SelectControlProps<AssetTimeseriesGroupBy>;
    aggregateFunction?: SelectControlProps<AggregateFunctions>;
    interval?: SelectControlProps<AggregateQueryInterval>;
}

/**
 * Defines the controls for configuring aggregate queries with interactive elements for a visualization component
 * Properties:
 * - `groupBy`: Control AggregateQueryInput.groupBy
 * - `aggregateFunction`: Control AggregateQueryInput.aggregateFunction
 */
export interface AggregateQueryControls {
    groupBy: (
        | SelectControlProps<TokenTimeseriesGroupBy>
        | SelectControlProps<AssetTimeseriesGroupBy>
    ) & {
        onChange: (
            updatedVal: AssetTimeseriesGroupBy | TokenTimeseriesGroupBy,
        ) => void;
    };
    aggregateFunction: SelectControlProps<AggregateFunctions> & {
        onChange: (updatedVal: AggregateFunctions) => void;
    };
    interval: SelectControlProps<AggregateQueryInterval> & {
        onChange: (updatedVal: AggregateQueryInterval) => void;
    };
}

// // // //

/**
 * Defines the configuration for enabling data export functionality in a visualization component
 * Property:
 * - `enabled`: Indicates if the data export feature is enabled.
 * - `ignoreFeatureFlag`: Indicates if the `enable-data-downloads` feature flag should be ignored (default: false)
 */
export interface DataExportControls {
    enabled: boolean;
    ignoreFeatureFlag?: boolean;
    filename?: string;
    exportTableName?:
        | ExportTableNames.asset_timeseries_v4
        | ExportTableNames.token_timeseries_v4;
}

// // // //

export interface ApiToolsControls {
    enabled: boolean;
    baseUrl: string;
}

// // // //

export enum YAxisTypes {
    linear = "linear", // Default in Highcharts
    logarithmic = "logarithmic",
    datetime = "datetime",
    category = "category",
}

export interface YAxisConfig {
    min?: number;
    type?: YAxisTypes;
    enabled?: boolean;
}

export interface ColumnPlotOptions {
    stacking?: "normal" | "percent" | null;
    pointPadding?: number;
    groupPadding?: number;
    borderRadius?: number;
}

export interface LinePlotOptions {
    marker?: {
        enabled?: boolean;
        states?: {
            hover?: {
                enabled?: boolean;
            };
        };
    };
}

export interface LegendProps {
    enabled?: boolean;
    align?: "left" | "right" | "center";
    layout?: "vertical" | "horizontal";
    verticalAlign?: "top" | "bottom";
    itemStyle?: React.CSSProperties;
    itemWidth?: number;
    maxHeight?: number;

    // NOTE - the below is all from Highcharts GPT. Stashed here for later reference
    // backgroundColor?: string | Highcharts.GradientColorObject | Highcharts.PatternObject;
    // borderColor?: string | Highcharts.GradientColorObject | Highcharts.PatternObject;
    // borderRadius?: number;
    // borderWidth?: number;
    // floating?: boolean;
    // itemCheckboxStyle?: Highcharts.CSSObject;
    // itemDistance?: number;
    // itemHiddenStyle?: Highcharts.CSSObject;
    // itemHoverStyle?: Highcharts.CSSObject;
    // itemMarginBottom?: number;
    // itemMarginTop?: number;
    // itemStyle?: Highcharts.CSSObject;
    // labelFormat?: string;
    // labelFormatter?: Highcharts.FormatterCallbackFunction<Highcharts.Series>;
    // lineHeight?: number;
    // margin?: number;
    // maxHeight?: number;
    // navigation?: Highcharts.LegendNavigationOptions;
    // padding?: number;
    // reversed?: boolean;
    // rtl?: boolean;
    // shadow?: boolean | Highcharts.CSSObject;
    // squareSymbol?: boolean;
    // symbolHeight?: number;
    // symbolPadding?: number;
    // symbolRadius?: number;
    // symbolWidth?: number;
    // title?: Highcharts.LegendTitleOptions;
    // useHTML?: boolean;
    // verticalAlign?: string;
    // width?: number;
    // x?: number;
    // y?: number;
}

// ENHANCEMENT - clean this up
// ENHANCEMENT - add ESDoc header for this
export interface VisualizationConfig {
    area?: boolean;
    seriesChartType?: ChartTypes;
    legend?: LegendProps;
    enableZoom?: boolean;
    showTotal?: boolean;
    height?: number;
    currencyValueDecimals?: number;
    yAxis?: YAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    linePlotOptions?: LinePlotOptions;
}

// // // //

export type MetricsToTimeSeriesViewFunction = (
    metrics: TimeSeriesItem[],
) => TimeSeriesView[];

export enum VisualizationModeTypes {
    area = "area",
    mintBurnVolume = "mint_burn_volume",
    apy = "apy",
    column = "column",
}

// ENHANCEMENT - clean this up
// ENHANCEMENT - add ESDoc header for this
export interface VisualizationMode {
    key: string;
    type: VisualizationModeTypes;
    content: {
        label: string;
        description?: string;
    };
    query: ApiQuery;
    endpoint:
        | ApiQueryEndpoints.AssetTimeseriesV4
        | ApiQueryEndpoints.TokenTimeseriesV4
        | null; // NOTE - null is used to indicate that the chart doesn't fetch data, should be removed
    visualizationConfig?: VisualizationConfig;
    visualizationControls?: VisualizationControlProps;
    queryControls?: AggregateQueryControlsProps;
    dataExportControls?: DataExportControls;
    apiTools?: ApiToolsControls;
    featureFlag?: FeatureNames;

    // Whether the user must be logged into to view the visualization
    loginRequired?: boolean;

    // // // //

    // metricsToSeries() function -> optional bypss to override default metricsToSeries behavior
    // to do things like make some series bar vs line, or modify colors, or groups, or styles/offsets/etc.
    metricsToTimeSeriesView?: MetricsToTimeSeriesViewFunction;

    // // // //

    // ENHANCEMENT - add custom controls component? <---- NOT SURE. Might be good to
    // Only potential use-case is switching measures -> but can't that be handled by modes?
    // is there a use case for switching measures that *COULDNT* be addressed by changing modes?
}
